import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Button, Card, Col, Form, Row, Spinner, InputGroup} from 'react-bootstrap'
import {Admin} from './core/_models'
import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {addUser, getAdminById, updateAdmin} from './core/_requests'
import {useNavigate, useParams} from 'react-router-dom'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import withReactContent from 'sweetalert2-react-content'
import Swal from 'sweetalert2'
import {Role} from '../manage-available-roles/core/_models'
import {getAllRoles} from '../manage-available-roles/core/_requests'

const ReactSwal = withReactContent(Swal)
const confirmDialog = (title: string) => {
  return ReactSwal.fire({
    title,
    icon: 'question',
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    customClass: {
      title: 'fw-bold',
      cancelButton: 'btn btn-danger btn-sm',
      confirmButton: 'btn btn-success btn-sm',
    },
    padding: '1rem',
  })
}

const ManageUserRoleDetailPage: FC<{subject: string}> = ({subject}) => {
  const axiosPrivate = useAxiosPrivate()
  const ability = useAbility(AbilityContext)
  const [isEditMode, setIsEditMode] = useState(false)
  const [roles, setRoles] = useState<Role[]>([])
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)
  const [isLoadingRole, setIsLoadingRole] = useState(false)
  const {idAdmin} = useParams()
  const isDetailMode = Boolean(idAdmin)
  const isAddMode = !idAdmin
  const navigate = useNavigate()
  const [admin, setAdmin] = useState<Admin>({
    id: '',
    username: '',
    email: '',
    role: '',
    password: '',
  })

  const fetchRoles = () => {
    if (isEditMode || isAddMode) {
      setIsLoadingRole(true)

      getAllRoles(axiosPrivate)
        .then((res) => {
          const resData = res.data.data
          setRoles(resData.roles)
        })
        .finally(() => {
          setIsLoadingRole(false)
        })
    }
  }

  const fetchAdminById = () => {
    if (!isDetailMode) return
    setIsLoadingRole(true)

    getAdminById(axiosPrivate, idAdmin)
      .then((res) => {
        const resData = res.data.data
        setAdmin(resData)
      })
      .finally(() => {
        setIsLoadingRole(false)
      })
  }

  const validateFormFields = () => {
    const requiredFields = ['username', 'email', 'economy', 'role']
    const isValid = requiredFields.every((field) => admin[field] !== '')
    setIsFormValid(isValid)
  }

  const handleCancel = () => {
    confirmDialog('Are you sure you want to cancel?').then((result) => {
      if (result.isConfirmed) {
        navigate('/manage-user-role')
      }
    })
  }

  const handleSubmit = () => {
    let payload = {}
    if (isAddMode) {
      payload = {
        username: admin.username,
        email: admin.email,
        password: admin.password,
        role: admin.role,
      }
    } else {
      payload = {
        role: admin.role,
      }
    }

    const message = isAddMode
      ? 'Are you sure you want to add this user?'
      : isEditMode
      ? 'Are you sure you want to update this user role?'
      : 'Invalid action'

    confirmDialog(message).then((result) => {
      if (!result.isConfirmed) return

      setIsLoadingButton(true)

      if (isEditMode) {
        updateAdmin(axiosPrivate, idAdmin, payload)
          .then((res) => {
            if (res.status === 200) {
              ReactSwal.fire({
                title: 'User role updated',
                icon: 'success',
                confirmButtonText: 'OK',
              }).then(() => {
                window.location.reload()
              })
            }
          })
          .catch((err) => {
            console.error(err)
            ReactSwal.fire('Error', 'Failed to update role')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      } else if (isAddMode) {
        addUser(axiosPrivate, payload)
          .then((res) => {
            if (res.status === 200) {
              ReactSwal.fire({
                title: 'New user added',
                icon: 'success',
                confirmButtonText: 'OK',
              }).then(() => {
                navigate('/manage-user-role')
              })
            }
          })
          .catch((err) => {
            console.error(err)
            ReactSwal.fire('Error', 'Failed to create new user')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      } else {
        ReactSwal.fire('Error', 'Invalid action')
      }
    })
  }

  const handleFormChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = e.target
    setAdmin((prevAdmin) => ({...prevAdmin, [name]: value}))
  }

  useEffect(() => {
    fetchRoles()
  }, [isEditMode, isDetailMode, isAddMode])

  useEffect(() => {
    validateFormFields()
  }, [admin])

  const generatePassword = () => {
    const length = 8
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const lower = 'abcdefghijklmnopqrstuvwxyz'
    const numbers = '0123456789'

    let password = ''
    password += upper.charAt(Math.floor(Math.random() * upper.length))
    password += lower.charAt(Math.floor(Math.random() * lower.length))
    password += numbers.charAt(Math.floor(Math.random() * numbers.length))

    for (let i = 4; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length))
    }

    // Shuffle password
    password = password
      .split('')
      .sort(() => 0.5 - Math.random())
      .join('')

    setAdmin((prev) => ({...prev, password: password}))
  }

  const copyToClipboard = () => {
    if (admin.password) {
      navigator.clipboard.writeText(admin.password)
      ReactSwal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Password copied!',
        showConfirmButton: false,
        timer: 1500,
      })
    }
  }

  useEffect(() => {
    fetchAdminById()
  }, [isEditMode, isDetailMode, isAddMode])

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Roles',
            path: 'manage-user-role',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {isAddMode ? 'Add New User' : isEditMode ? 'Edit' : admin.username}
      </PageTitle>
      <Row className='mb-5'>
        <Col>
          <Card>
            <Card.Header className='card-header-stretch'>
              <Card.Title className='w-100'>
                <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                  <div className='title d-flex align-items-center'>
                    <span className='fa fa-file-alt px-2'></span>
                    <h3 className='text-gray-700'>
                      {isAddMode ? 'Add New User' : isEditMode ? 'Edit' : admin.username}
                    </h3>
                  </div>
                  {isDetailMode && ability.can('update', subject) && !isEditMode ? (
                    <span className='d-inline-block'>
                      <Button
                        className='btn btn-primary d-flex gap-4 align-items-center'
                        onClick={() => setIsEditMode(true)}
                        style={{pointerEvents: isEditMode ? 'none' : 'auto'}} // important
                      >
                        <span className='fa fa-pencil'></span>
                        Edit
                      </Button>
                    </span>
                  ) : (
                    ''
                  )}
                </div>
              </Card.Title>
            </Card.Header>
            <Card.Body>
              {!isLoadingRole ? (
                <>
                  {isEditMode ? (
                    <Form className='form-role-detail'>
                      <div className='row'>
                        <div className='col-12'>
                          <h3>User Information</h3>
                        </div>
                        <div className='col-md-6'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Username
                          </Form.Label>
                          <div className='data-value'>{admin.username}</div>
                        </div>
                        <div className='col-md-6'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Email
                          </Form.Label>
                          <div className='data-value'>{admin.email}</div>
                        </div>
                        <div className='col-md-6'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Economy
                          </Form.Label>
                          <div className='data-value'>{admin.economy ?? '-'}</div>
                        </div>
                        <div className='col-md-6'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            <span className='required'>Role</span>
                          </Form.Label>
                          <Form.Select name='role' onChange={handleFormChange}>
                            <option value='' disabled>
                              Select Role
                            </option>
                            {roles.map((role) => (
                              <option
                                key={role.id}
                                value={role.id}
                                selected={role.role === admin.role}
                              >
                                {role.role}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                      </div>
                      <hr />
                      <div className='row pt-5'>
                        <div className='col-lg-12 d-flex justify-content-end gap-3'>
                          <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                            Cancel
                          </Button>
                          <Button
                            variant='primary'
                            onClick={handleSubmit}
                            disabled={isLoadingButton || !isFormValid}
                          >
                            {isLoadingButton ? (
                              <>
                                <Spinner animation='border' size='sm' className='me-2' />
                                Submit
                              </>
                            ) : (
                              'Submit'
                            )}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  ) : isAddMode ? (
                    <Form className='form-role-add'>
                      <div className='row'>
                        <div className='col-12'>
                          <h3>User Information</h3>
                        </div>
                        <div className='col-md-6'>
                          <Form.Label className='required text-primary me-0 ps-0' column>
                            Username
                          </Form.Label>
                          <Form.Control
                            type='text'
                            placeholder='Username'
                            name='username'
                            value={admin.username}
                            onChange={handleFormChange}
                          />
                        </div>
                        <div className='col-md-6'>
                          <Form.Label className='required text-primary me-0 ps-0' column>
                            Email
                          </Form.Label>
                          <Form.Control
                            type='email'
                            placeholder='Email'
                            name='email'
                            value={admin.email}
                            onChange={handleFormChange}
                          />
                        </div>
                        <div className='col-md-6'>
                          <Form.Label className='required text-primary me-0 ps-0' column>
                            Password
                          </Form.Label>
                          <InputGroup>
                            <Form.Control
                              type='password'
                              placeholder='Password'
                              name='password'
                              value={admin.password}
                              onChange={handleFormChange}
                            />
                            <Button variant='secondary' onClick={generatePassword}>
                              Generate
                            </Button>
                            <Button
                              variant='secondary'
                              onClick={copyToClipboard}
                              disabled={!admin.password}
                            >
                              Copy
                            </Button>
                          </InputGroup>
                        </div>
                        <div className='col-md-6'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            <span className='required'>Role</span>
                          </Form.Label>
                          <Form.Select name='role' onChange={handleFormChange}>
                            <option value='' selected>
                              Select Role
                            </option>
                            {roles.map((role) => (
                              <option
                                key={role.id}
                                value={role.id}
                                selected={role.role === admin.role}
                              >
                                {role.role}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                      </div>
                      <hr />
                      <div className='row pt-5'>
                        <div className='col-lg-12 d-flex justify-content-end gap-3'>
                          <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                            Cancel
                          </Button>
                          <Button
                            variant='primary'
                            onClick={handleSubmit}
                            disabled={isLoadingButton || !isFormValid}
                          >
                            {isLoadingButton ? (
                              <>
                                <Spinner animation='border' size='sm' className='me-2' />
                                Submit
                              </>
                            ) : (
                              'Submit'
                            )}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  ) : (
                    <div>
                      <div className='row'>
                        <div className='col-12'>
                          <h3>General Information</h3>
                        </div>
                        <div className='col-md-6 mb-3'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Username
                          </Form.Label>
                          <div className='data-value'>{admin.username}</div>
                        </div>
                        <div className='col-md-6 mb-3'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Email
                          </Form.Label>
                          <div className='data-value'>{admin.email}</div>
                        </div>
                        <div className='col-md-6 mb-3'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Economy
                          </Form.Label>
                          <div className='data-value'>{admin.economy ?? '-'}</div>
                        </div>
                        <div className='col-md-6 mb-3'>
                          <Form.Label className='text-primary me-0 ps-0' column>
                            Role
                          </Form.Label>
                          <div className='data-value'>{admin.role}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div
                  className='d-flex align-items-center justify-content-center'
                  style={{height: '50vh'}}
                >
                  <Spinner animation='border' role='status' variant='primary' className='me-3' />
                  <div className='loading-text'>
                    Loading
                    <span className='dot'>.</span>
                    <span className='dot'>.</span>
                    <span className='dot'>.</span>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default ManageUserRoleDetailPage
